import { updatePermissions } from '@/ability/helpers';
import clearUserLocalStorage from '@/utils/clearUserLocalStorage';
import {
  ExternalUser, PERSISTED_USER_FIELDS, PersistedUser, User
} from '@/models/User';
import router from '@/router';
import AuthenticationService from '@/services/AuthenticationService';
import useTokenState from '@/state/tokenState';
import getJSONFromLocalStorage from '@/utils/getJSONFromLocalStorage';
import { useLocalStorage } from '@vueuse/core';
import moment from 'moment';
import { RawLocation } from 'vue-router';
import { Module } from 'vuex';

const { accessToken, clear: clearToken, refreshToken } = useTokenState();

// setUser projects every incoming user onto PERSISTED_USER_FIELDS so in-memory
// state and localStorage hold exactly those fields and nothing more — any extra
// field on the source object is dropped. This is the single chokepoint every
// caller (login, registration, settingsState re-commit) flows through, and it
// keeps the rehydrated-from-storage user identical to the freshly-set one.
const pickPersistedUser = (user: User | ExternalUser | null): PersistedUser | null => {
  if (!user) return null;
  const source = user as Record<string, unknown>;
  return PERSISTED_USER_FIELDS.reduce((acc, key) => {
    if (source[key] !== undefined) acc[key] = source[key];
    return acc;
  }, {} as Record<string, unknown>) as PersistedUser;
};

const handleAuthSuccess = (commit: any, data: any) => {
  commit('authSuccess', { token: data.token });
  commit('setUser', data.externalUser);
  refreshToken.value = data.refreshToken;

  updatePermissions((data.externalUser?.roles ?? []), (data.externalUser?.permissions ?? []));
};

interface State {
  user: PersistedUser | null;
  redirectUrl: RawLocation | null;
}

const module: Module<State, any> = {
  state: {
    user: getJSONFromLocalStorage('user'),
    redirectUrl: null,
  },
  getters: {
    user: (state) => state.user,
    getRedirectUrl: (state) => state.redirectUrl,
  },
  mutations: {
    authSuccess(state, data) {
      accessToken.value = data.token;
      refreshToken.value = data.refreshToken;
    },

    clearUserAndToken(state) {
      state.user = null;
      localStorage.removeItem('user');
      clearToken();
    },

    setUser(state, user: User | ExternalUser) {
      const persistedUser = pickPersistedUser(user);
      const storedUser = useLocalStorage('user', JSON.stringify(persistedUser));
      state.user = persistedUser;
      storedUser.value = JSON.stringify(persistedUser);
    },

    setRedirectUrl(state, data: RawLocation | null) {
      state.redirectUrl = data;
    }
  },
  actions: {
    login({ commit, getters }, params: {
      email: string;
      password: string;
    }) {
      return new Promise<void>((resolve, reject) => {
        AuthenticationService.loginWithCredentials(
          params.email,
          params.password,
        )
          .then(async (data) => {
            handleAuthSuccess(commit, data);

            if (data.metaData) {
              if (data.metaData.mobilephoneNumber) {
                commit('mobileNumber', { mobileNumber: data.metaData.mobilephoneNumber });
              }
              if (data.metaData.registrationProcessId) {
                commit('registrationProcessId', {
                  registrationProcessId: data.metaData.registrationProcessId,
                });
              }
              commit('registrationStepPlain', { registrationStepPlain: data.metaData.nextRegistrationStep });
              const expiresAt = moment().add(process.env.VUE_APP_REGISTRATION_EXPIRES_AFTER, 'minutes').format();
              commit('expiresAt', { expiresAt });
              router.push({ name: getters.registrationStepPlainName });
            } else if (getters.getRedirectUrl) {
              router.push(getters.getRedirectUrl);
              commit('setRedirectUrl', null);
            } else {
              router.push({ name: 'home' });
            }

            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    loginWithToken({ commit }, params: {
      token: string;
    }) {
      return new Promise<void>((resolve, reject) => {
        AuthenticationService.loginWithToken(params.token)
          .then((data) => {
            handleAuthSuccess(commit, data);
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    logout({ commit }) {
      commit('clearUserAndToken');
      clearUserLocalStorage();
      sessionStorage.clear();
    },
  },
};

export default module;
